import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  Avatar,
  Button,
  VStack,
  HStack,
  Input,
  Icon,
  CloseButton,
  InputGroup,
  InputRightElement,
  Flex,
} from "@chakra-ui/react";
import { FaComments } from "react-icons/fa";
import { IoSend } from "react-icons/io5";
import "./style.css";
const BASE_URL = process.env.REACT_APP_DEPLOYED_BASE_URL;
const token = localStorage.getItem("token");

const prompts = [
  {
    id: 1,
    question:
      "Describe your content style (funny, educational, cinematic, vlog-style, etc.) and the tone or vibe you want your emails to have (e.g., professional, playful, confident, humble, etc.)",
    key: "contentStyle",
  },
  {
    id: 2,
    question:
      "Share with me the brand name and product(s) you're interested in and why you’re interested in working with the brand (e.g., you already use the product, it aligns with your values/aesthetic, your audience would love it)",
    key: "brandInterest",
  },
  {
    id: 3,
    question:
      "Provide the number of emails you would like in your sequence (we recommend 4)",
    key: "emailCount",
  },
];

const SashaBotChat = ({ onFinish, onClose }) => {
  const userName = localStorage.getItem("userName") || "";
  const bottomRef = useRef(null);
  const [step, setStep] = useState(0); // 0 = intro
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState("");
  const [finished, setFinished] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    {
      from: "bot",
      text: `Hi ${userName}! I can create an effective sequence and help you get the brand's attention. Click the 𝘴𝘵𝘢𝘳𝘵 button to begin and fill out the 3 prompts to assist me.`,
    },
  ]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  const handleClose = () => {
    // Clear all state
    setStep(0);
    setAnswers({});
    setInput("");
    setFinished(false);
    setIsTyping(false);
    setChatHistory([
      {
        from: "bot",
        text: `Hi ${userName}! I can create an effective sequence and help you get the brand's attention. Click the 𝘴𝘵𝘢𝘳𝘵 button to begin and fill out the 3 prompts to assist me.`,
      },
    ]);
    const universalBotToggle = document.querySelector(".sasha-chatbot-toggle");
    const generalChatbox = document.querySelector(".sasha-chatbox");
    if (universalBotToggle) {
      universalBotToggle.style.display = "flex"; // or "block" depending on your layout
    }
    if (generalChatbox) {
      generalChatbox.style.display = "flex"; // or "block" depending on your layout
    }
    onClose?.(); // signal parent to hide the bot
  };

  const handleNext = () => {
    if (step === 0) {
      setChatHistory((prev) => [
        ...prev,
        { from: "user", text: "Start" },
        { from: "bot", text: prompts[0].question },
      ]);
      setStep(1);
      return;
    }

    const currentPrompt = prompts[step - 1];
    const answerKey = currentPrompt.key;
    const updatedAnswers = { ...answers, [answerKey]: input };
    setAnswers(updatedAnswers);

    const isLast = step === prompts.length;

    const newHistory = [{ from: "user", text: input }];

    if (isLast) {
      const emailCount = input || "a few";
      if (answerKey === "emailCount") {
        const count = parseInt(input, 10);

        if (isNaN(count) || count < 1 || count > 4) {
          setChatHistory((prev) => [
            ...prev,
            { from: "user", text: input },
            {
              from: "bot",
              text: "Please enter a valid number between 1 and 4.",
            },
          ]);
          setInput("");
          return; // ⛔️ stop here, don’t proceed
        }
      }

      newHistory.push({
        from: "bot",
        text: `Thanks for the help. I'll create a ${emailCount}-email sequence tailored to your needs.`,
      });

      setFinished(true);
      setIsTyping(true);
      setChatHistory((prev) => [
        ...prev,
        ...newHistory,
        { from: "bot", text: "Sasha is typing...", type: "typing" },
      ]);
      setInput("");

      const payload = {
        content_style: updatedAnswers.contentStyle,
        product_info: updatedAnswers.brandInterest,
        email_count: Number(updatedAnswers.emailCount),
      };

      fetch(BASE_URL + "/auto_sequence_generator/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((data) => {
          setIsTyping(false);
          setChatHistory((prev) => [
            ...prev.filter((msg) => msg.type !== "typing"),
            {
              from: "bot",
              text:
                data?.message ||
                "Thanks for the help. Give me a few minutes to generate your sequence.",
            },
          ]);
          onFinish?.(updatedAnswers);
        })
        .catch((err) => {
          console.error("API Error:", err);
          setIsTyping(false);
          setChatHistory((prev) => [
            ...prev.filter((msg) => msg.type !== "typing"),
            {
              from: "bot",
              text: "Oops! Something went wrong while generating your sequence. Please try again later.",
            },
          ]);
        });
    } else {
      const nextPrompt = prompts[step];
      newHistory.push({ from: "bot", text: nextPrompt.question });

      setChatHistory((prev) => [...prev, ...newHistory]);
      setInput("");
      setStep(step + 1);
    }
  };

  return (
    <>
      <Box
        position="fixed"
        bottom="100px"
        left={["50%", null, "auto"]}
        transform={["translateX(-50%)", null, "none"]}
        right={[null, null, "20px"]}
        w="90vw"
        maxW="360px"
        h="513px"
        bg="white"
        border="1px solid #ccc"
        borderRadius="8px"
        boxShadow="0 0 10px rgba(0, 0, 0, 0.15)"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        zIndex="1000"
      >
        {/* 🔒 Fixed Header (top part, not sticky) */}
        <Box bg="#1e1917" px={3} py={2} borderTopRadius="md">
          <Flex align="center">
            <Flex align="center" justify="center" flex="1">
              <Icon as={FaComments} color="white" mr={2} />
              <Text fontWeight="bold" color="white">
                Sasha Chat
              </Text>
            </Flex>
          </Flex>
        </Box>

        {/* 💬 Scrollable Chat Area */}
        <Box
          flex="1"
          overflowY="auto"
          px={3}
          py={2}
          sx={{
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c4c4c4", // or use theme color
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a0a0a0",
            },
          }}
        >
          <VStack spacing={3} align="stretch">
            {chatHistory.map((entry, index) => (
              <HStack
                key={index}
                alignSelf={entry.from === "user" ? "flex-end" : "flex-start"}
              >
                {entry.from === "bot" && entry.type !== "startPrompt" && (
                  <Avatar
                    name="Sasha"
                    size="sm"
                    src="/images/SashaImage.jpeg"
                  />
                )}
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    mb={1}
                    color={entry.from === "user" ? "gray.600" : "gray.400"}
                    textAlign={entry.from === "user" ? "right" : "left"}
                    pr={entry.from === "user" ? "18px" : "0"} //  pushes it slightly left
                  >
                    {entry.from === "user" ? "You" : "Sasha"}
                  </Text>

                  <Box
                    bg={entry.from === "user" ? "gray.100" : "black"}
                    color={entry.from === "user" ? "black" : "white"}
                    px={3}
                    py={2}
                    borderRadius="md"
                    maxW="260px"
                  >
                    {entry.type === "typing" ? (
                      <Text fontSize="sm">
                        <span className="typing-dots">
                          <span>.</span>
                          <span>.</span>
                          <span>.</span>
                        </span>
                      </Text>
                    ) : (
                      <Text fontSize="sm">{entry.text}</Text>
                    )}
                  </Box>
                </Box>
              </HStack>
            ))}
            <div ref={bottomRef} />
          </VStack>
        </Box>

        {/* ⌨️ Input Composer */}
        {step > 0 && step <= prompts.length && !finished && (
          <Box borderTop="1px solid #eee" p={3}>
            <InputGroup size="md">
              <Input
                placeholder="Compose your message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && input.trim()) {
                    handleNext();
                  }
                }}
                borderRadius="md"
                pr="3rem"
              />
              <InputRightElement width="3rem">
                <Button
                  h="1.75rem"
                  size="sm"
                  onClick={handleNext}
                  isDisabled={!input.trim()}
                  bg="black"
                  _hover={{ bg: "gray.700" }}
                  color="white"
                  borderRadius="md"
                >
                  <IoSend size="16" />
                </Button>
              </InputRightElement>
            </InputGroup>
          </Box>
        )}

        {/* 👇 Start button if still on intro */}
        {step === 0 && (
          <Box p={3} borderTop="1px solid #eee">
            <HStack justify="flex-end">
              <Button size="sm" colorScheme="blue" onClick={handleNext}>
                Start
              </Button>
            </HStack>
          </Box>
        )}
      </Box>
      {/* Bubble Button with X */}
      <Box
        position="fixed"
        bottom="28px"
        right="30px"
        w="45px"
        h="45px"
        borderRadius="full"
        bg="#4482b3"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="md"
        zIndex="1001"
        cursor="pointer"
        onClick={handleClose}
      >
        <Text fontSize="24px" fontWeight="500">
          X
        </Text>
      </Box>
    </>
  );
};

export default SashaBotChat;
